'use client';
import { motion } from 'framer-motion';
import { ArrowDown, MessageCircle, Mail, Download } from 'lucide-react';
import { Github, Linkedin } from '@/components/ui/Icons';
import Image from 'next/image';

const WHATSAPP = process.env.NEXT_PUBLIC_WHATSAPP_NUMBER || '923001234567';
const EMAIL = process.env.NEXT_PUBLIC_EMAIL || 'your@email.com';

export default function Hero() {
  return (
    <section id="hero" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      overflow: 'hidden',
      paddingTop: '5rem',
    }}>
      {/* Background glow orbs */}
      <div className="glow-orb" style={{ width: '600px', height: '600px', background: 'var(--accent)', top: '-200px', right: '-200px' }} />
      <div className="glow-orb" style={{ width: '400px', height: '400px', background: 'var(--accent-hover)', bottom: '-100px', left: '-150px' }} />

      <div className="container" style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8rem', flexWrap: 'wrap-reverse' }}>
        <div style={{ maxWidth: '700px', flex: '1 1 500px' }}>
          {/* Greeting */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: '30px',
              padding: '0.4rem 1rem',
              marginBottom: '1.5rem',
            }}
          >
            <span style={{ fontSize: '1rem' }}>👋</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontFamily: 'Outfit, sans-serif' }}>Hello, I am</span>
          </motion.div>

          {/* Name */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            style={{
              fontSize: 'clamp(2.5rem, 6vw, 5rem)',
              fontWeight: 800,
              lineHeight: 1.1,
              marginBottom: '0.5rem',
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            Muhammad Umer
            <br />
            <span className="gradient-text">Shah Farooqi</span>
          </motion.h1>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}
          >
            <div style={{ width: '40px', height: '3px', background: 'var(--accent)', borderRadius: '2px' }} />
            <p style={{
              fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
              color: 'var(--text-muted)',
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 500,
            }}>Full Stack Developer</p>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            style={{
              fontSize: '1.05rem',
              color: 'var(--text-muted)',
              lineHeight: 1.8,
              marginBottom: '2.5rem',
              maxWidth: '550px',
            }}
          >
            I build robust, scalable web applications using <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Django</span> and <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Next.js</span>.
            Passionate about clean code, great UX, and solving real-world problems.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '3rem' }}
          >
            <a href="#projects" className="btn btn-primary" id="hero-view-work">
              View My Work
              <ArrowDown size={16} />
            </a>
            <a
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              id="hero-whatsapp"
            >
              <MessageCircle size={16} />
              WhatsApp Me
            </a>
          </motion.div>

          {/* Social Links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
          >
            <span style={{ color: 'var(--text-subtle)', fontSize: '0.85rem', fontFamily: 'Outfit, sans-serif' }}>Find me on</span>
            {[
              { icon: <Github size={18} />, href: 'https://github.com/', label: 'GitHub' },
              { icon: <Linkedin size={18} />, href: 'https://linkedin.com/', label: 'LinkedIn' },
              { icon: <Mail size={18} />, href: `mailto:${EMAIL}`, label: 'Email' },
            ].map(({ icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                style={{
                  width: '40px', height: '40px', borderRadius: '10px',
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'var(--text-muted)', transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                {icon}
              </a>
            ))}
          </motion.div>
        </div>
        
        {/* Profile Picture */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          style={{ flex: '1 1 300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <div style={{ 
            width: 'min(100%, 400px)', 
            aspectRatio: '1', 
            borderRadius: '30px', 
            overflow: 'hidden', 
            border: '2px solid var(--border)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 0 0 8px var(--bg-surface), 0 0 40px var(--accent-glow)',
            transform: 'rotate(2deg)',
            transition: 'transform 0.3s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'rotate(0deg) scale(1.02)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'rotate(2deg) scale(1)'}
          >
            <Image 
              src="/profile.png" 
              alt="Muhammad Umer Shah Farooqi" 
              width={500} 
              height={500} 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              priority
            />
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        style={{
          position: 'absolute', bottom: '2rem', left: '50%',
          transform: 'translateX(-50%)', color: 'var(--text-subtle)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem',
        }}
      >
        <span style={{ fontSize: '0.75rem', fontFamily: 'Outfit, sans-serif', letterSpacing: '2px', textTransform: 'uppercase' }}>Scroll</span>
        <ArrowDown size={16} />
      </motion.div>
    </section>
  );
}
